/** @jsxImportSource @emotion/react */

import { FC, useCallback, useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import { Chip } from '@mui/material'
import { ApolloError, useApolloClient } from '@apollo/client'
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg'
import { Search } from 'components/Input'
import * as questionListClasses from 'pagesNew/Questions/List/styles'
import Table, {
  BulkActions,
  FiltersWrapper,
  FooterWrapper,
  HeaderWrapper,
  PaginationPage,
  PaginationTake,
} from 'components/Table'
import Select from 'components/Select'
import { BulkAction } from 'pagesNew/Questions/List'
import { findAllForCMSTask_findAllForCMSTask_items as Task } from '__generatedTypes__/findAllForCMSTask'
import { BULK_REMOVE_TASK, BULK_RESTORE_TASK, BULK_SOFT_REMOVE_TASK } from 'queries/customSchedule'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { toast } from 'utils/toast'
import { convertSecondsToHHMM, Format } from 'utils/convertSecondsToHHMM'
import { TaskModal } from '../TaskModal/TaskModal'
import { useTaskModal } from '../TaskModal/hooks'
import { TASK_TYPE_TITLE } from '../consts'
import * as classes from './styles'
import { useDeletedTasks, usePublishedTasks, useTasksCount } from './useTasks'
import { ALL_SUBJECT_ID, useTaskFilters } from './useTaskFilters'

export const getActionSubtitle = (action: string, count: number) =>
  `Are you sure you want to ${action} ${count > 1 ? `these ${count} tasks` : 'this task'}?`

const BULK_ACTION_TRASH: BulkAction = {
  id: 'trash',
  title: 'Trash',
  getTitle: () => 'Move to Trash',
  getSubtitle: (count?: number) => getActionSubtitle('move to trash', count ?? 1),
  mutationName: BULK_SOFT_REMOVE_TASK,
}

const BULK_ACTION_RESTORE: BulkAction = {
  id: 'restore',
  title: 'Restore',
  getTitle: () => 'Restore',
  getSubtitle: (count?: number) => getActionSubtitle('restore', count ?? 1),
  mutationName: BULK_RESTORE_TASK,
}

const BULK_ACTION_DELETE: BulkAction = {
  id: 'delete',
  title: 'Delete',
  getTitle: () => 'Permanently Delete',
  getSubtitle: (count?: number) => getActionSubtitle('permanently delete', count ?? 1),
  mutationName: BULK_REMOVE_TASK,
}

const BULK_ACTIONS_PUBLISHED: BulkAction[] = [BULK_ACTION_TRASH]
const BULK_ACTIONS_DELETED: BulkAction[] = [BULK_ACTION_RESTORE, BULK_ACTION_DELETE]

type Props = {
  platformId: string
}

export const List: FC<Props> = ({ platformId }) => {
  const { mutate } = useApolloClient()

  const { type: typeFilters, subject: subjectFilters } = useTaskFilters(platformId)

  const tasksCount = useTasksCount(platformId)

  const [isDeletedSelected, setIsDeletedSelected] = useState(false)
  const [confirmBulkAction, setConfirmBulkAction] = useState<BulkAction | null>(null)
  const [confirmItemAction, setConfirmItemAction] = useState<{ action: BulkAction; item: Task } | null>(null)

  const [selectedItems, setSelectedItems] = useState<Task[]>([])
  const publishedTasksState = usePublishedTasks(platformId)
  const deletedTasksState = useDeletedTasks(platformId)
  const selectedTasksState = isDeletedSelected ? deletedTasksState : publishedTasksState
  const { items, count, loading, refetch, pagination, search, order, filters, onFilterChange } = selectedTasksState

  const {
    isOpen: isTaskModalOpen,
    initialValues: taskModalInitialValues,
    onOpen: onOpenTaskModal,
    onClose: onCloseTaskModal,
    onSubmit: onSubmitTask,
    onDelete: onDeleteTask,
  } = useTaskModal(platformId, () => Promise.all([refetch(), tasksCount.refetch()]))

  useEffect(() => {
    deletedTasksState.refetch()
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  useEffect(() => {
    if (!loading) {
      setConfirmItemAction(null)
      setConfirmBulkAction(null)
      setSelectedItems([])
    }
  }, [loading])

  const handleApplyItemAction = useCallback(async () => {
    if (!confirmItemAction) {
      return
    }

    setConfirmItemAction(null)

    try {
      await mutate({
        mutation: confirmItemAction.action.mutationName,
        variables: {
          ids: [{ id: confirmItemAction.item.id }],
        },
      })
      toast.success('Action applied successfully')
    } catch (error) {
      console.error('An error occurred while trying to apply action', error)
      if (error && typeof error === 'object' && 'graphQLErrors' in error) {
        return toast.error((error as ApolloError).graphQLErrors?.[0]?.message as string)
      }

      toast.error('Failed to apply action')
    }
    refetch()
    tasksCount.refetch()
  }, [confirmItemAction, refetch, tasksCount.refetch])

  const handleApplyBulkAction = useCallback(async () => {
    if (!selectedItems.length || !confirmBulkAction) {
      return
    }

    setConfirmBulkAction(null)
    setSelectedItems([])
    try {
      await mutate({
        mutation: confirmBulkAction.mutationName,
        variables: {
          ids: selectedItems.map(({ id }) => ({ id })),
        },
      })
      toast.success('Action applied successfully')
    } catch (error) {
      console.error('An error occurred while trying to apply action', error)
      if (error && typeof error === 'object' && 'graphQLErrors' in error) {
        return toast.error((error as ApolloError).graphQLErrors?.[0]?.message as string)
      }
      toast.error('Failed to apply action')
    }
    refetch()
    tasksCount.refetch()
  }, [confirmBulkAction, selectedItems, refetch, tasksCount.refetch])

  const getActionButton = useCallback(
    (action: BulkAction, item: Task) => (
      <Button
        variant="text"
        size="small"
        color="blue"
        css={questionListClasses.action}
        onClick={() => setConfirmItemAction({ action, item })}
        disabled={loading}
      >
        {action.title}
      </Button>
    ),
    [setConfirmItemAction, loading]
  )

  return (
    <>
      <div css={classes.headerWrapper}>
        <div css={classes.headerButtonsWrapper}>
          <Button color="blue" variant="contained" css={classes.newTaskButton} onClick={() => onOpenTaskModal()}>
            <PlusIcon />
            New Task
          </Button>
        </div>
      </div>

      <div css={questionListClasses.toolbarWrapper}>
        <div css={questionListClasses.tabsWrapper}>
          <Button
            size="small"
            variant="text"
            onClick={() => setIsDeletedSelected(false)}
            color={isDeletedSelected ? 'gray' : 'blue'}
            css={questionListClasses.action}
          >
            Published ({tasksCount.published})
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => setIsDeletedSelected(true)}
            color={isDeletedSelected ? 'blue' : 'gray'}
            css={questionListClasses.action}
          >
            Deleted ({tasksCount.deleted})
          </Button>
        </div>
        <Search {...search} css={questionListClasses.fullWidthForMobile} />
      </div>

      <HeaderWrapper css={questionListClasses.filters}>
        <FiltersWrapper className="wrapper">
          <BulkActions
            bulkActions={isDeletedSelected ? BULK_ACTIONS_DELETED : BULK_ACTIONS_PUBLISHED}
            onApply={setConfirmBulkAction}
            disabled={!selectedItems.length || loading}
          />
          <Select
            items={subjectFilters.items}
            value={filters.subject ?? null}
            onChange={(value) => onFilterChange('subject', value ?? undefined)}
            placeholder="All Subjects"
            onInputChange={subjectFilters.search}
            loading={subjectFilters.loading}
            displaySelectedItemsFirst
            searchable
            css={questionListClasses.fullWidthForMobile}
          />
          <Select
            items={typeFilters.items}
            value={filters.type ?? null}
            onChange={(value) => onFilterChange('type', value ?? undefined)}
            placeholder="All Types"
            displaySelectedItemsFirst
            css={questionListClasses.fullWidthForMobile}
          />
        </FiltersWrapper>
        <PaginationPage totalCount={count ?? 0} {...pagination} css={questionListClasses.pagination} />
      </HeaderWrapper>

      <Table
        css={questionListClasses.tableWrapper}
        columns={[
          {
            title: 'Title',
            renderCell: (task) => (
              <div css={questionListClasses.titleWrapper}>
                <div>{task.title}</div>
                {isDeletedSelected ? (
                  <div className="actions">
                    {getActionButton(BULK_ACTION_RESTORE, task)}
                    {getActionButton(BULK_ACTION_DELETE, task)}
                  </div>
                ) : (
                  <div className="actions">
                    <Button
                      variant="text"
                      size="small"
                      color="blue"
                      css={questionListClasses.action}
                      onClick={() => onOpenTaskModal(task)}
                      disabled={loading}
                    >
                      Edit
                    </Button>
                    {getActionButton(BULK_ACTION_TRASH, task)}
                  </div>
                )}
              </div>
            ),
            flex: 2,
          },
          {
            title: 'Subject',
            renderCell: (task) => {
              if (task.subject) {
                return (
                  <div css={classes.chipWrapper(task.subject.tabName)}>
                    <Chip label={task.subject?.tabName} css={classes.subjectChip(task.subject.tabName)} />
                  </div>
                )
              }

              return (
                <div css={classes.chipWrapper(ALL_SUBJECT_ID)}>
                  <Chip label="All" css={classes.subjectChip(ALL_SUBJECT_ID)} />
                </div>
              )
            },
            flex: 2,
          },
          {
            title: 'Type',
            renderCell: (task) => TASK_TYPE_TITLE[task.type],
          },
          {
            title: 'Time',
            renderCell: (task) => (task.time ? convertSecondsToHHMM(task.time, Format.Short) : '-'),
          },
          {
            title: 'Date Created',
            renderCell: (task) => new Date(task.createdAt).toLocaleDateString(),
            flex: '0 1 124px',
            orderField: 'createdAt',
          },
        ]}
        items={items}
        selectedItems={selectedItems}
        order={order.value}
        onOrder={order.onChange}
        onSelect={setSelectedItems}
        isSelectable
        wrapperMinWidth={1100}
      />

      <FooterWrapper>
        <PaginationTake {...pagination} css={questionListClasses.paginationTake} />
      </FooterWrapper>

      <ConfirmationModal
        open={Boolean(confirmBulkAction && selectedItems.length)}
        title={confirmBulkAction?.getTitle(selectedItems.length) ?? ''}
        subtitle={confirmBulkAction?.getSubtitle(selectedItems.length) ?? ''}
        onClose={() => setConfirmBulkAction(null)}
        onConfirm={handleApplyBulkAction}
      />

      <ConfirmationModal
        open={Boolean(confirmItemAction)}
        title={confirmItemAction?.action.getTitle() ?? ''}
        subtitle={confirmItemAction?.action.getSubtitle() ?? ''}
        onClose={() => setConfirmItemAction(null)}
        onConfirm={handleApplyItemAction}
      />

      <TaskModal
        isOpen={isTaskModalOpen}
        initialValues={taskModalInitialValues}
        platformId={platformId}
        onClose={onCloseTaskModal}
        onSubmit={onSubmitTask}
        onDelete={onDeleteTask}
      />
    </>
  )
}
